import { Component, OnInit } from '@angular/core';
import { TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { take, delay, switchMap, tap } from 'rxjs/operators';
import * as moment from 'moment-mini';
import { QcReportScheduleSchedule } from 'src/models';
import { AimsCommonTableHeader, AimsCommonTableHeaderType, AimsCommonTablePermissions, TableRowDelete, TableRowUpdate } from '@aims/common-components';
import { GetTextPipe } from 'src/app/pipes';

@Component({
  selector: 'report-schedule',
  templateUrl: './report-schedule.component.html',
  styleUrls: ['./report-schedule.component.scss'],
  providers: [GetTextPipe]
})
export class ReportScheduleComponent implements OnInit {

  // AimsCommonTable setup
  tablePermissions: AimsCommonTablePermissions = {
    create: this.canAdd(),
    update: this.canUpdate(),
    delete: this.canDelete(),
    export: true,
    print: false,
  };
  tableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'reportScheduleId', label: 'Report Number', type: 'dynamic', width: 90 },
    { key: 'reportScheduleName', label: 'Name', type: 'string', editable: false, width: 400 },
    { key: 'reportScheduleFrequency', label: 'Frequency', type: 'string', editable: false, width: 100 },
    { key: 'dayDue', label: 'Day Due', type: 'string', editable: false, width: 80 },
    { key: 'responsibleManager', label: 'Responsible Manager', type: 'string', editable: false, },
    { key: 'dateDue', label: 'Date Due', type: 'date', },
    { key: 'dateSubmittedToQc', label: 'Date To QC', type: 'date', },
    { key: 'dateSubmittedToAf', label: 'Date To AF', type: 'date', },
  ];

  defaultRow: QcReportScheduleSchedule = new QcReportScheduleSchedule();

  constructor(public transferHttp: TransferHttpService,
    public permissions: PermissionsService, public getTextPipe: GetTextPipe) {
  }

  user = this.transferHttp.getCache("user");

  //Spinner
  spinnerText: string = "Loading Report Schedules...";
  showSpinner: boolean = false;

  years: Array<number> = [
    2007,
    2008,
    2009,
    2010,
    2011,
    2012,
    2013,
    2014,
    2015,
    2016,
    2017,
    2018,
    2019,
    2020,
    2021,
    2022,
    2023,
    2024,
    2025
  ];

  months = [
    { name: 'January', number: 0 },
    { name: 'February', number: 1 },
    { name: 'March', number: 2 },
    { name: 'April', number: 3 },
    { name: 'May', number: 4 },
    { name: 'June', number: 5 },
    { name: 'July', number: 6 },
    { name: 'August', number: 7 },
    { name: 'September', number: 8 },
    { name: 'October', number: 9 },
    { name: 'November', number: 10 },
    { name: 'December', number: 11 }
  ];

  month: BehaviorSubject<number> = new BehaviorSubject(moment().month());
  year: BehaviorSubject<number> = new BehaviorSubject(moment().year());

  excelReportName = this.buildExportName();
  printReportName: string = this.buildReportName();

  //Main Grid
  monthReportSchedule = combineLatest([this.month, this.year]).pipe(
    delay(0), //This is here to prevent an expression changed after checked error
    tap(ret => this.showSpinner = true),
    switchMap(([month, year]) => this.transferHttp.getHot(`QualityControl/GetMonthReportSchedule?month=${month}&year=${year}`)),
    tap(ret => this.showSpinner = false)
  );

  //DropDown Values
  reportScheduleValues = this.transferHttp.getSingle('QualityControl/GetReportScheduleForPopUp');

  reportScheduleLookupColumns:AimsCommonTableHeader[] = [
    { key: 'reportScheduleNumber', label: 'Report Number', filter: true, type: AimsCommonTableHeaderType.STRING },
    { key: 'reportScheduleName', label: 'Name', filter: true, type: AimsCommonTableHeaderType.STRING },
    { key: 'reportScheduleFrequency', label: 'Frequency', filter: true, type: AimsCommonTableHeaderType.STRING },
    { key: 'dayDue', label: 'Day Due', filter: true, type: AimsCommonTableHeaderType.STRING },
    { key: 'active', label: 'Active', filter: true, type: AimsCommonTableHeaderType.BOOLEAN }
  ];

  //***************************
  // Functions
  //***************************

  updateReportName(): void {
    this.reportScheduleValues.subscribe();
    this.excelReportName = this.buildExportName();
    this.printReportName = this.buildReportName();
  }

  buildExportName() {
    return "ReportScheduleExport_" + this.months.map(function (o) { return o.name; })[this.month.value].substring(0, 3) + "_" + this.year.value;
  }

  buildReportName() {
    return `/reporting/ReportSchedule/trdp/${JSON.stringify({ Month: this.month.value + 1, Year: this.year.value })}`;
  }

  monthChange(month: number): void {
    this.month.pipe(take(1)).subscribe(m => {
      if (month != m) {
        this.month.next(month);
        this.updateReportName();
      }
    });
  }

  yearChange(year: number): void {
    this.year.pipe(take(1)).subscribe(y => {
      if (year != y) {
        this.year.next(year);
        this.updateReportName();
      }
    });
  }

  public canUpdate(): boolean {
    return (this.permissions.canUpdate(this.permissions.ComponentID.ReportSchedules));
  }
  public canAdd(): boolean {
    return (this.permissions.canCreate(this.permissions.ComponentID.ReportSchedules));
  }
  public canDelete(): boolean {
    return (this.permissions.canDelete(this.permissions.ComponentID.ReportSchedules));
  }

  // grid handlers
  public handleRowUpdate(update: TableRowUpdate): void {
    this.saveRowHandler(update.newRow);
  }

  public handleRowDelete(deleted: TableRowDelete) {
    let row: QcReportScheduleSchedule = deleted.row as QcReportScheduleSchedule;

    this.spinnerText = "Deleting...";
    this.showSpinner = true;
    this.transferHttp.delete('QualityControl/DeleteReportScheduleSchedule?reportScheduleSubmitId=' + row.reportScheduleSubmitId).pipe(take(1)).subscribe(ret => {
      this.month.pipe(take(1)).subscribe(m => {
        this.month.next(moment(row.dateDue).month());
        this.year.next(moment(row.dateDue).year());
        this.showSpinner = false;
      });
    });
  }

  public handleRowAdded(update: TableRowUpdate){
    const item: QcReportScheduleSchedule = update.newRow as QcReportScheduleSchedule;

    this.saveRowHandler(update.newRow);
  }

  public saveRowHandler(dataItem ) {
    this.spinnerText = "Saving...";
    this.showSpinner = true;

    this.transferHttp.put('QualityControl/SaveReportScheduleSchedules', dataItem).pipe(take(1)).subscribe(ret => {
      this.month.pipe(take(1)).subscribe(m => {
        this.month.next(moment(dataItem.dateDue).month());
        this.year.next(moment(dataItem.dateDue).year());
       this.showSpinner = false;
      });
    });
  }

getForExcel = (data: any) => {
    var repSchedVals: Array<object>;
    this.reportScheduleValues.pipe(take(1)).subscribe(p => {
      repSchedVals = p;
    });

    var exportData = data.map(r => {
      var x = {
        'reportScheduleId': this.getTextPipe.transform(r.reportScheduleId, 'reportScheduleId','reportScheduleNumber', repSchedVals),
        'reportScheduleName': r.reportScheduleName,
        'reportScheduleFrequency': r.reportScheduleFrequency,
        'dayDue': r.dayDue,
        'responsibleManager': r.responsibleManager,
        'dateDue': r.dateDue,
        'dateSubmittedToQc': r.dateSubmittedToQc,
        'dateSubmittedToAf': r.dateSubmittedToAf,
      };
      return x;
    });
    return { 'data': exportData, 'fileName':  this.excelReportName };
  }

  ngOnInit() {
  }

}
